import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { MatLegacyButton as MatButton } from '@angular/material/legacy-button';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Data } from '@angular/router';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { AccountService } from 'app/core';
import { User, UserAssociation } from 'app/core/models';
import { Account } from 'app/core/models/account.types';
import { UserService } from 'app/layout/common/user/user.service';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { InviteUserComponent } from 'app/modules/settings/modals/invite-user/invite-user.component';
import { noop, Observable, Subject } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { Dialog } from '@angular/cdk/dialog';

@Component({
  selector: 'pipeline-user-associations',
  templateUrl: './pipeline-user-associations.component.html',
  styleUrls: ['./pipeline-user-associations.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PipelineUserAssociationsComponent extends OnDestroyMixin implements OnInit, OnDestroy {
  @Input() pipelineId: number;
  private _unsubscribeAll: Subject<void>;
  private _userAssociationsPanelOverlayRef: OverlayRef;
  @ViewChild('userAssociationsPanel')
  private _userAssociationsPanel: TemplateRef<any>;
  @ViewChild('userAssociationsPanelOrigin')
  private _userAssociationsPanelOrigin: MatButton;
  private _userAssociationsPanelTemplatePortal: TemplatePortal;

  data: any;
  account: Account;
  accountUserAssociations: UserAssociation[];
  userAssociations: UserAssociation[];
  filteredUserAssociations: UserAssociation[] = [];
  public isCurrentUserAdmin: boolean = false;
  public currentUser: User;
  associatedUserIds: any[] = [];
  userRole: string;

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _overlay: Overlay,
    private _viewContainerRef: ViewContainerRef,
    private _projectsService: ProjectsService,
    private _accountService: AccountService,
    private _userService: UserService,
    private dialog: Dialog,
    private readonly changeDetector: ChangeDetectorRef,
  ) {
    super();
    this._unsubscribeAll = new Subject();
  }

  ngOnInit(): void {
    this._activatedRoute.data.subscribe((data: Data) => {
      this.data = data;
    });

    this._accountService.currentAccount.subscribe((accountData) => {
      this.account = accountData;
    });

    this._projectsService.pipeline$
      .pipe(
        untilComponentDestroyed(this),
        switchMap(() => this.loadUserAssociations()),
      )
      .subscribe(noop);

    this._accountService.userAssociations$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((userAssociations: UserAssociation[]) => {
        this.accountUserAssociations = userAssociations;
        this.filteredUserAssociations = userAssociations;
        this.checkIfAdmin();
      });

    this._userService.role$.pipe(takeUntil(this._unsubscribeAll)).subscribe((role: string) => {
      if (role) {
        this.userRole = role;
      }
    });
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions

    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();

    // Dispose the overlays if they are still on the DOM
    if (this._userAssociationsPanelOverlayRef) {
      this._userAssociationsPanelOverlayRef.dispose();
    }
    super.ngOnDestroy();
  }

  loadUserAssociations(): Observable<any> {
    return this._projectsService.getUserAssociationsGeneral('Pipeline', this.pipelineId).pipe(
      tap((userAssociations) => {
        if (userAssociations) {
          this.userAssociations = userAssociations;
          this.associatedUserIds = this.userAssociations.map((ua) => ua.user.id);
          this.changeDetector.detectChanges();
        }
      }),
    );
  }

  checkIfAdmin(): void {
    if (this.currentUser && this.accountUserAssociations) {
      let foundUserAssociations = this.accountUserAssociations.filter((ua) => {
        return ua.user.id == this.currentUser.id;
      });
      if (foundUserAssociations && foundUserAssociations.length > 0) {
        this.isCurrentUserAdmin = true;
      }
    }
  }

  /**
   * Filter tags
   *
   * @param event
   */
  filterUserAssociations(event): void {
    // Get the value
    const value = event.target.value.toLowerCase();

    // Filter the tags
    this.filteredUserAssociations = this.accountUserAssociations.filter((ua) =>
      ua.user.full_name?.toLowerCase().includes(value),
    );
  }

  openInvitationModal(): void {
    this._closeUserAssociationsPanel();

    const dialogRef = this.dialog.open(InviteUserComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: { role: 'guest', extra: { pipeline_id: this.pipelineId } },
    });

    dialogRef.closed.subscribe(noop);
  }

  openUserAssociationsPanel(): void {
    // Create the overlay
    this._userAssociationsPanelOverlayRef = this._overlay.create({
      backdropClass: '',
      hasBackdrop: true,
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(this._userAssociationsPanelOrigin._elementRef.nativeElement)
        .withFlexibleDimensions()
        .withViewportMargin(64)
        .withLockedPosition()
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
        ]),
    });

    // Subscribe to the attachments observable
    this._userAssociationsPanelOverlayRef.attachments().subscribe(() => {
      // Add a class to the origin
      //this._renderer2.addClass(this._fieldDefsPanelOrigin.nativeElement, 'panel-opened');

      // Focus to the search input once the overlay has been attached
      this._userAssociationsPanelOverlayRef.overlayElement.querySelector('input').focus();
    });

    // Create a portal from the template
    this._userAssociationsPanelTemplatePortal = new TemplatePortal(this._userAssociationsPanel, this._viewContainerRef);

    // Attach the portal to the overlay
    this._userAssociationsPanelOverlayRef.attach(this._userAssociationsPanelTemplatePortal);

    // Subscribe to the backdrop click
    this._userAssociationsPanelOverlayRef.backdropClick().subscribe(() => {
      this._closeUserAssociationsPanel();
    });
  }

  /**
   * Close the event panel
   *
   * @private
   */
  private _closeUserAssociationsPanel(): void {
    // Remove the class from the origin
    //this._renderer2.removeClass(this._fieldDefsPanelOrigin.nativeElement, 'panel-opened');

    // If overlay exists and attached...
    if (this._userAssociationsPanelOverlayRef && this._userAssociationsPanelOverlayRef.hasAttached()) {
      // Detach it
      this._userAssociationsPanelOverlayRef.detach();

      // Reset the tag filter
      this.filteredUserAssociations = this.accountUserAssociations;
    }

    // If template portal exists and attached...
    if (this._userAssociationsPanelTemplatePortal && this._userAssociationsPanelTemplatePortal.isAttached) {
      // Detach it
      this._userAssociationsPanelTemplatePortal.detach();
    }
  }

  /**
   * Toggle display column
   *
   * @param tag
   * @param change
   */
  toggleUserAssociation(userAssociation: any, change: MatCheckboxChange): void {
    if (change.checked) {
      this.addUserAssociation(userAssociation);
    } else {
      this.removeUserAssociation(userAssociation);
    }
  }

  /**
   * Filter tags input key down event
   *
   * @param event
   */
  filterUserAssociationsInputKeyDown(event): void {
    // Return, if the pressed key is not 'Enter'
    if (event.key !== 'Enter') {
      return;
    }

    // If there is a tag...
    const ua = this.filterUserAssociations[0];
    const isFieldVisible = this.userAssociations.find((item) => item.user.full_name === ua.user.full_name);

    // If the found tag is already applied to the contact...
    if (isFieldVisible) {
      // Remove the tag from the contact
      this.removeUserAssociation(ua);
    } else {
      // Otherwise add the tag to the contact
      this.addUserAssociation(ua);
    }
  }

  removeUserAssociation(userAssociation): void {
    const index = this.userAssociations.findIndex((item) => item.user.id === userAssociation.user.id);
    let ua = this.userAssociations[index];

    this._projectsService
      .deleteUserAssociationGeneral(ua)
      .pipe(switchMap(() => this.loadUserAssociations()))
      .subscribe(() => this.changeDetector.detectChanges());
  }

  addUserAssociation(userAssociation): void {
    //add them to the pipeline with the same role as they have on the account
    let ua = {
      related_to_id: this.pipelineId,
      related_to_type: 'Pipeline',
      user_id: userAssociation.user.id,
      role: userAssociation.role,
    };
    this._projectsService
      .createUserAssociationGeneral(ua)
      .pipe(switchMap(() => this.loadUserAssociations()))
      .subscribe(() => this.changeDetector.detectChanges());
  }

  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  trackByFn(index: number, item: any): any {
    return item.id || index;
  }
}
